import {
    Button,
    Container,
    Form,
    FormGroup,
    Input,
    InputPlaceholder,
    Segment,
    SegmentContent,
    SegmentHeader,
    Select,
} from '@/platform/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Deferred, Link, useForm, usePage } from '@inertiajs/react';
import { useMemo } from 'react';
import YearValueList from './components/YearValueList';

const CreateOrUpdateView = () => {
    const { sector_parameter, sectors } = usePage().props;
    const { data, setData, errors, clearErrors, post, patch, processing } =
        useForm({
            ...(sector_parameter || {}),
            children: (sector_parameter?.children || []).map(child => ({
                ...child,
                key: `existing-${child.id}`,
            })),
        });

    const sectorOptions = useMemo(
        () =>
            Object.keys(sectors || {}).map(sectorId => ({
                id: sectorId,
                name: sectors[sectorId],
            })),
        [sectors]
    );

    // Child errors are keyed by index, so they go stale as soon as rows are
    // added or removed. Drop them rather than show them against the wrong row.
    const clearChildErrors = () => {
        const staleKeys = Object.keys(errors || {}).filter(key =>
            key.startsWith('children.')
        );

        if (staleKeys.length) {
            clearErrors(...staleKeys);
        }
    };

    const handleSubmit = () => {
        if (!sector_parameter?.id) {
            post('/sector-parameters');

            return;
        }

        patch(`/sector-parameters/${sector_parameter.id}`);
    };

    const title = useMemo(
        () => (sector_parameter?.id ? 'Wijzig parameter' : 'Nieuwe parameter'),
        [sector_parameter]
    );

    return (
        <Container>
            <Form onSubmit={handleSubmit}>
                <Segment>
                    <SegmentHeader header={title}>
                        <Button as={Link} href='/sector-parameters'>
                            <ArrowLeftIcon className='size-5' />
                            Terug
                        </Button>
                    </SegmentHeader>
                    <SegmentContent>
                        <FormGroup>
                            {sector_parameter?.unique_id && (
                                <Input
                                    label='UUID'
                                    description='Wordt automatisch gegenereerd en gebruikt door berekeningen.'
                                    readOnly
                                    value={sector_parameter.unique_id}
                                />
                            )}
                            <Input
                                label='Naam'
                                required
                                value={data?.name || ''}
                                onChange={event =>
                                    setData('name', event?.target?.value)
                                }
                                error={errors?.name}
                            />
                            <Deferred
                                data='sectors'
                                fallback={
                                    <InputPlaceholder
                                        label='Sector'
                                        placeholder='Sectoren worden geladen...'
                                    />
                                }
                            >
                                <Select
                                    label='Sector'
                                    placeholder='Geen sector'
                                    options={sectorOptions}
                                    value={data?.sector_id || ''}
                                    onChange={event =>
                                        setData(
                                            'sector_id',
                                            event?.target?.value
                                        )
                                    }
                                    error={errors?.sector_id}
                                />
                            </Deferred>
                            <Input
                                label='Waarde'
                                description='Vaste waarde die geldt wanneer er geen jaarwaarde is.'
                                value={data?.value || ''}
                                onChange={event =>
                                    setData('value', event?.target?.value)
                                }
                                error={errors?.value}
                            />
                        </FormGroup>
                    </SegmentContent>
                </Segment>

                <YearValueList
                    items={data?.children}
                    sectorOptions={sectorOptions}
                    defaultSectorId={data?.sector_id}
                    errors={errors}
                    onChange={setData}
                    onStructureChange={clearChildErrors}
                />

                <Segment floating>
                    <SegmentContent right>
                        <Button
                            type='submit'
                            variant='primary'
                            loading={processing}
                        >
                            Opslaan
                        </Button>
                    </SegmentContent>
                </Segment>
            </Form>
        </Container>
    );
};

export default CreateOrUpdateView;
